import { AfterViewInit, Directive, effect, ElementRef, inject, input, OnDestroy } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { TextClampUtilitiesService } from '@utilities/text-clamp-utilites.service';

@Directive({
  selector: '[appTextClampTooltip]',
})
export class TextClampTooltipDirective implements AfterViewInit, OnDestroy {
  public readonly appTextClampTooltip = input.required<string>();
  public readonly defaultTooltip = input<string | null>(null);

  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly matTooltip = inject(MatTooltip);
  private readonly textClampUtilitiesService = inject(TextClampUtilitiesService);

  private resizeObserver: ResizeObserver | undefined;
  private intersectionObserver: IntersectionObserver | undefined;
  private isInitialized = false;

  constructor() {
    effect(() => {
      this.appTextClampTooltip();
      this.defaultTooltip();

      if (!this.isInitialized) {
        return;
      }

      this.applyTooltipStateWhenVisible();
    });
  }

  public ngAfterViewInit(): void {
    this.isInitialized = true;

    this.applyTooltipStateWhenVisible();
  }

  public ngOnDestroy(): void {
    this.teardownVisibilityObservers();
  }

  private applyTooltipStateWhenVisible(): void {
    setTimeout(() => {
      const element = this.elementRef.nativeElement;

      if (this.isEffectivelyVisible(element)) {
        this.applyTooltipState();

        return;
      }

      this.setupVisibilityObservers(element);
    });
  }

  private applyTooltipState(): void {
    const isClamped = this.textClampUtilitiesService.isTextClamped(this.elementRef);

    if (isClamped) {
      this.matTooltip.message = `${this.appTextClampTooltip()} ${this.defaultTooltip() ? `(${this.defaultTooltip()})` : ''}`;
      this.matTooltip.disabled = false;
    } else {
      this.matTooltip.message = this.defaultTooltip() ?? '';
      this.matTooltip.disabled = !this.defaultTooltip();
    }
  }

  private isEffectivelyVisible(element: HTMLElement): boolean {
    if (!element.isConnected) {
      return false;
    }

    if (element.getClientRects().length > 0) {
      return true;
    }

    if (element.offsetParent !== null) {
      return true;
    }

    const computed = window.getComputedStyle(element);

    if (computed.display === 'none' || computed.visibility === 'hidden') {
      return false;
    }

    return element.clientWidth > 0 || element.clientHeight > 0;
  }

  private setupVisibilityObservers(element: HTMLElement): void {
    this.teardownVisibilityObservers();

    this.resizeObserver = new ResizeObserver(() => {
      if (this.isEffectivelyVisible(element)) {
        this.applyTooltipState();
        this.teardownVisibilityObservers();
      }
    });
    this.resizeObserver.observe(element);

    this.intersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting || this.isEffectivelyVisible(element)) {
          this.applyTooltipState();
          this.teardownVisibilityObservers();
          break;
        }
      }
    });
    this.intersectionObserver.observe(element);
  }

  private teardownVisibilityObservers(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = undefined;
    }
  }
}
